/**
 * The wire protocol's fixed numbers, pinned to literals by the protocol codec suite so a value
 * cannot drift with the tests still green.
 */
export const SOMNIO_PROTOCOL_CONSTANTS = {
  /**
   * Strict equality at the hello gate on both sides: a mismatch in either direction rejects the
   * connection rather than degrading it. Bump it when the wire breaks.
   */
  helloVersion: 15,
  maxFrameLength: 1 << 20,

  /** UTF-8 byte cap for `nickname` and `email` on login/registration, for item ids, and for the record ids inside a sector. */
  maxIdentifierUTF8Bytes: 64,
  /** UTF-8 byte cap for `password`. */
  maxPasswordUTF8Bytes: 128,
  /** Minimum UTF-8 byte floor for a registration password. */
  minPasswordUTF8Bytes: 8,
  /** UTF-8 byte cap for chat and admin-say text. */
  maxSayUTF8Bytes: 256,
  /**
   * UTF-8 cap for `redeemSession`/`revokeSession` tokens. A base64url 256-bit token is 43
   * characters, so this is headroom; the point is that a tampered or oversized value in
   * `localStorage` is refused here instead of costing a round trip.
   */
  maxSessionTokenUTF8Bytes: 256,
  /** UTF-8 byte cap for a runtime entity id, which for an NPC embeds its sector's name. */
  maxEntityIdUTF8Bytes: 320,
  /**
   * UTF-8 byte cap for a sector's name, which is also an interior's space id. It keeps
   * `npc:<sector>/<id>` inside `maxEntityIdUTF8Bytes` with an npc id at the identifier cap, and
   * no sector file reaches it: a 255-byte file name leaves 241 for the stem of a `.somnio-sector`.
   */
  maxSectorNameUTF8Bytes: 251,

  /** Bound on the absolute value of any coordinate, in metres. */
  maxCoordinateMetres: 10_000,
  /** Bound on a sector's width and depth, in metres. */
  maxSectorExtentMetres: 512,
  maxSectorPlacements: 4096,
  maxSectorBlockers: 4096,
  maxSectorDoors: 4096,
  /** The ground marks of one sector, and the points of all of them together, both capped before any mark is decoded. */
  maxSectorGroundMarks: 512,
  maxSectorGroundPoints: 8192,
  /**
   * How wide every ground mark's edge fades, centred on the edge. One constant rather than a value
   * per mark, so no mark can be painted with a hard edge.
   */
  markFeatherMetres: 0.6,
  /** The most the noise that breaks a mark's edge moves it, past the feather. */
  markEdgeNoiseMetres: 0.375,
  /** The widest a stroke mark may be painted, either side of its line; a small sector caps it lower. */
  maxMarkStrokeRadiusMetres: 10,
  /** The most Healers who hold one raise at once, which bounds the list a `raising` frame names. */
  maxRaiseHealers: 32,
  /** The most rows an `inventory` frame carries, generous over the handful of items a dreamer holds. */
  maxInventoryRows: 32,
  /** The most ranks a `lucidity` frame carries, generous over the teachings there are. */
  maxLucidityRanks: 32,
  /** The most entities one `moves` frame carries; the server splits a larger batch across frames. */
  maxMovesPerFrame: 256,

  /**
   * Slack the WebSocket-layer frame ceiling keeps above the encoder guard, so an oversized
   * message throws at encode time rather than the receiver hard-closing.
   */
  frameSizeSlack: 64,
} as const;

/** The WebSocket-layer frame ceiling (`ws`'s `maxPayload`): the encoder guard plus the slack. */
export const MAX_WIRE_FRAME_SIZE = SOMNIO_PROTOCOL_CONSTANTS.maxFrameLength + SOMNIO_PROTOCOL_CONSTANTS.frameSizeSlack;

/**
 * UTF-8 byte length. `String.prototype.length` counts UTF-16 code units, so it disagrees
 * with every byte cap above the moment a field carries a non-ASCII character (an umlaut is
 * one code unit but two UTF-8 bytes; an emoji is two code units but four bytes). Every
 * cap check must route through here.
 */
export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * Truncates `value` to at most `maxBytes` UTF-8 bytes without splitting a character.
 * `TextDecoder` in non-fatal mode would emit a replacement character for a severed
 * sequence, so the cut is walked back to a code-point boundary instead.
 */
export function truncateToUTF8Bytes(value: string, maxBytes: number): string {
  const encoded = new TextEncoder().encode(value);
  if (encoded.length <= maxBytes) return value;
  let end = maxBytes;
  // 0b10xxxxxx marks a UTF-8 continuation byte; walk back off one to land on a lead byte.
  while (end > 0 && (encoded[end]! & 0xc0) === 0x80) end -= 1;
  return new TextDecoder().decode(encoded.subarray(0, end));
}
